import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Plumbing() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/12938c72166d9a030b0b2e60abca3714.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Plumbing Training Program
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto">
            You've Got The Pipes - Start Your Plumbing Career Today
          </p>
          <Button href="/contact" size="lg">
            Enroll Now
          </Button>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-6">Program Overview</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Learn plumbing basics online—pipe fitting, drainage, safety—through Texas Workforce Academy's entry-level course. Gain skills and certification to start your journeyman plumbing career with confidence.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our comprehensive program covers everything from residential plumbing systems to commercial installations, preparing you for a stable and rewarding career in this essential trade.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=plumbing%20training%20classroom%20students%20learning%20pipe%20fitting%20and%20installation%20techniques%20hands%20on%20practice%20modern%20educational%20setting%20simple%20clean%20background%20bright%20lighting%20professional%20realistic%20photography&width=800&height=600&seq=plumbing2&orientation=landscape"
                alt="Plumbing Training"
                className="rounded-lg shadow-xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">What You'll Learn</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Pipe Fitting & Installation",
                description: "Master the techniques for cutting, threading, and joining various types of pipes including copper, PVC, and PEX."
              },
              {
                title: "Drainage Systems",
                description: "Learn proper drainage system design, installation, and maintenance for residential and commercial buildings."
              },
              {
                title: "Water Supply Systems",
                description: "Understand water distribution, pressure regulation, and fixture installation for efficient water delivery."
              },
              {
                title: "Safety Protocols",
                description: "Master OSHA safety standards, proper tool usage, and hazard prevention in plumbing work."
              },
              {
                title: "Code Compliance",
                description: "Learn local and national plumbing codes, permit requirements, and inspection procedures."
              },
              {
                title: "Troubleshooting & Repair",
                description: "Develop diagnostic skills to identify and fix common plumbing problems efficiently."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-check-line text-2xl text-blue-900"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Career Opportunities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              "Journeyman Plumber",
              "Residential Plumber",
              "Commercial Plumber",
              "Service Plumber",
              "Pipefitter",
              "Plumbing Contractor"
            ].map((career, index) => (
              <div key={index} className="flex items-center bg-gray-50 p-6 rounded-lg">
                <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-briefcase-line text-xl text-blue-900"></i>
                </div>
                <span className="text-lg font-semibold text-black">{career}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Details */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Program Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Online Learning</h3>
              <p className="text-gray-700">Flexible online courses that fit your schedule and learning pace</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-medal-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Certification Ready</h3>
              <p className="text-gray-700">Comprehensive preparation for journeyman plumber certification exams</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Practical Skills</h3>
              <p className="text-gray-700">Learn with real plumbing scenarios and virtual instruction</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Plumbing Career?</h2>
          <p className="text-xl text-white mb-8">
            Join our program today and become a skilled plumbing professional.
          </p>
          <Button href="/contact" size="lg">
            Contact Us Today
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
